import { fetchAuthSession } from '@aws-amplify/auth';

import { CREDITS_ENGINE_API } from '../env';

// whisperbi-credits-engine es un stack SAM independiente con su propia
// API Gateway (no comparte RestApiId con agents-admin, a diferencia de
// billing-admin) — igual que BillingService: solo autenticación Cognito
// (Bearer ID token), sin x-api-key.
const API_BASE = CREDITS_ENGINE_API;

const defaultHeaders = {
  Accept: 'application/json'
};

async function getAuthHeader() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    const idToken = tokens?.idToken?.toString();
    if (idToken) {
      return { Authorization: `Bearer ${idToken}` };
    }
  } catch (error) {
    console.error('❌ CreditsEngineService.getAuthHeader: unable to fetch session', error);
  }
  return {};
}

async function request(path, init = {}) {
  const authHeader = await getAuthHeader();

  const headers = {
    ...defaultHeaders,
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    ...(init.headers || {}),
    ...authHeader
  };

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  let response;
  try {
    response = await fetch(url, { ...init, headers, mode: 'cors', cache: 'no-cache' });
  } catch (networkError) {
    throw new Error(`No se pudo contactar el motor de créditos (${url}). ${networkError.message}`);
  }

  const requestId =
    response.headers.get('x-amzn-requestid') ||
    response.headers.get('x-amz-apigw-id') ||
    response.headers.get('x-request-id') ||
    'n/a';

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    let technicalMessage = errorBody || `HTTP ${response.status}`;
    try {
      const parsed = JSON.parse(errorBody);
      technicalMessage = parsed.error || parsed.message || technicalMessage;
    } catch {
      // Response body wasn't JSON — keep the raw text.
    }

    const error = new Error(technicalMessage);
    error.technicalMessage = `HTTP ${response.status} (reqId=${requestId}): ${technicalMessage}`;
    error.status = response.status;
    error.requestId = requestId;
    throw error;
  }

  if (response.status === 204) {
    return null;
  }

  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function buildQuery(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, value);
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * DynamoDB Decimal fields llegan serializadas como STRING o INT desde el
 * backend (`decimal_utils.py` usa `default=str` para lo no entero) — ej.
 * "1.00" en vez de 1.0. Usar esto al leer cualquier campo numérico de una
 * respuesta de CreditsEngineService.
 */
export function toNumber(value, fallback = 0) {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

const TRANSACTION_TYPE_LABELS = {
  RESERVATION: { label: 'Reserva', color: 'default' },
  CAPTURE: { label: 'Consumo', color: 'primary' },
  RELEASE: { label: 'Liberación', color: 'default' },
  REFUND: { label: 'Reembolso', color: 'info' },
  PURCHASE: { label: 'Compra', color: 'success' },
  ALLOCATION: { label: 'Asignación', color: 'success' },
  BONUS: { label: 'Bono', color: 'success' },
  EXPIRATION: { label: 'Expiración', color: 'warning' },
  ADJUSTMENT: { label: 'Ajuste manual', color: 'warning' },
  OVERAGE: { label: 'Sobregiro', color: 'error' }
};

export function formatTransactionType(type) {
  return TRANSACTION_TYPE_LABELS[type] || { label: type || '—', color: 'default' };
}

const CreditsEngineService = {
  // --- Saldo, ledger, límites y uso de la propia cuenta (o de cualquiera si root) ---
  async getBalance({ accountId } = {}) {
    return request(`/credits/balance${buildQuery({ account_id: accountId })}`, { method: 'GET' });
  },
  // Los listados paginados devuelven `{ items, nextCursor }` -- misma
  // forma que AuditService.listInvocations, que es el patrón de referencia.
  // `nextCursor` es null cuando ya no quedan páginas. Antes cada uno de
  // estos métodos desenvolvía una clave fija con `|| []` y tiraba el resto
  // de la respuesta, así que el cursor del backend se habría perdido en
  // silencio.
  async getLedger({ accountId, userId, limit = 50, cursor } = {}) {
    const response = await request(
      `/credits/ledger${buildQuery({ account_id: accountId, user_id: userId, limit, cursor })}`,
      { method: 'GET' }
    );
    return {
      events: Array.isArray(response?.events) ? response.events : [],
      nextCursor: response?.next_cursor || null
    };
  },
  async getConfiguration({ accountId } = {}) {
    return request(`/credits/configuration${buildQuery({ account_id: accountId })}`, { method: 'GET' });
  },
  async getLimits({ accountId } = {}) {
    return request(`/credits/limits${buildQuery({ account_id: accountId })}`, { method: 'GET' });
  },
  async getThroughput({ accountId } = {}) {
    return request(`/credits/throughput${buildQuery({ account_id: accountId })}`, { method: 'GET' });
  },
  async getUsage({ accountId, start, end } = {}) {
    return request(`/credits/usage${buildQuery({ account_id: accountId, start, end })}`, { method: 'GET' });
  },
  async getUsageBuckets({ accountId, bucket = 'day', start, end, userId } = {}) {
    const response = await request(
      `/credits/usage-buckets${buildQuery({ account_id: accountId, bucket, start, end, user_id: userId })}`,
      { method: 'GET' }
    );
    return response?.buckets || [];
  },

  // --- Ajuste manual de saldo (root) ---
  async adjustBalance(payload) {
    return request('/admin/balance-adjustment', { method: 'POST', body: JSON.stringify(payload) });
  },

  // --- Precios de IA por modelo (root, versionado) ---
  async listModelPricing({ provider, model, limit, cursor } = {}) {
    const response = await request(
      `/admin/model-pricing${buildQuery({ provider, model, limit, cursor })}`,
      { method: 'GET' }
    );
    return {
      items: Array.isArray(response?.pricing) ? response.pricing : [],
      nextCursor: response?.next_cursor || null
    };
  },
  async upsertModelPricing(payload) {
    return request('/admin/model-pricing', { method: 'PUT', body: JSON.stringify(payload) });
  },

  // --- Precios por tipo de operación (root) ---
  async listOperationPricing() {
    const response = await request('/admin/operation-pricing', { method: 'GET' });
    return response?.operation_pricing || [];
  },
  async upsertOperationPricing(payload) {
    return request('/admin/operation-pricing', { method: 'PUT', body: JSON.stringify(payload) });
  },

  // --- Configuración de créditos (root, versionado, GLOBAL o account_id) ---
  async listCreditConfig({ scope, limit, cursor } = {}) {
    const response = await request(
      `/admin/credit-configuration${buildQuery({ scope, limit, cursor })}`,
      { method: 'GET' }
    );
    return {
      items: Array.isArray(response?.credit_config) ? response.credit_config : [],
      nextCursor: response?.next_cursor || null
    };
  },
  async upsertCreditConfig(payload) {
    return request('/admin/credit-configuration', { method: 'PUT', body: JSON.stringify(payload) });
  },

  // --- Política de crédito de cuenta (lectura root-o-admin-propio, escritura root) ---
  async getAccountPolicy(accountId) {
    const response = await request(
      `/admin/account-credit-policy${buildQuery({ account_id: accountId })}`,
      { method: 'GET' }
    );
    return response || {};
  },
  async upsertAccountPolicy(payload) {
    return request('/admin/account-credit-policy', { method: 'PUT', body: JSON.stringify(payload) });
  },

  // --- Política de crédito por usuario (root o admin de su propia cuenta) ---
  async listUserPolicies(accountId, { limit, cursor } = {}) {
    const response = await request(
      `/admin/user-credit-policy${buildQuery({ account_id: accountId, limit, cursor })}`,
      { method: 'GET' }
    );
    return {
      items: Array.isArray(response?.policies) ? response.policies : [],
      nextCursor: response?.next_cursor || null
    };
  },
  async upsertUserPolicy(payload) {
    return request('/admin/user-credit-policy', { method: 'PUT', body: JSON.stringify(payload) });
  },
  async deleteUserPolicy(accountId, userId) {
    return request(
      `/admin/user-credit-policy${buildQuery({ account_id: accountId, user_id: userId })}`,
      { method: 'DELETE' }
    );
  },

  // --- Planes de suscripción (lectura root-o-admin, escritura root) ---
  async listSubscriptionPlans({ limit, cursor } = {}) {
    const response = await request(
      `/admin/subscription-plan${buildQuery({ limit, cursor })}`,
      { method: 'GET' }
    );
    return {
      items: Array.isArray(response?.plans) ? response.plans : [],
      nextCursor: response?.next_cursor || null
    };
  },
  async upsertSubscriptionPlan(payload) {
    return request('/admin/subscription-plan', { method: 'PUT', body: JSON.stringify(payload) });
  },

  // --- Analítica y consumo cross-tenant (root) ---
  async getCreditAnalytics({ accountId, start, end } = {}) {
    return request(
      `/admin/credit-analytics${buildQuery({ account_id: accountId, start, end })}`,
      { method: 'GET' }
    );
  },
  async getAdminUsageBuckets({ accountId, bucket = 'day', start, end } = {}) {
    const response = await request(
      `/admin/usage-buckets${buildQuery({ account_id: accountId, bucket, start, end })}`,
      { method: 'GET' }
    );
    return response?.buckets || [];
  },

  // --- Costo INTERNO de infraestructura por tenant (root) -- registro de
  // uso (Lambda, API Gateway, DynamoDB, S3), no créditos. start/end en
  // YYYY-MM-DD; sin ellos el backend usa el mes en curso.
  async getInfrastructureUsage({ start, end } = {}) {
    return request(`/admin/usage${buildQuery({ start, end })}`, { method: 'GET' });
  },

  // Alertas de costo por tenant (umbral diario y picos) y su configuración.
  async getInfrastructureAlerts({ start, end } = {}) {
    const response = await request(`/admin/usage-alerts${buildQuery({ start, end })}`, { method: 'GET' });
    return response?.alerts || [];
  },

  async getInfrastructureAlertsConfig() {
    return request('/admin/usage-alerts/config', { method: 'GET' });
  },

  async updateInfrastructureAlertsConfig(config) {
    return request('/admin/usage-alerts/config', { method: 'PUT', body: JSON.stringify(config) });
  },

  // --- Expiración manual de batches (root) ---
  async expireBatches(accountId) {
    return request('/admin/expire-batches', { method: 'POST', body: JSON.stringify({ account_id: accountId }) });
  },

  // --- Catálogo público de planes (cualquier usuario autenticado, para la
  // comparativa de upgrade en el chat) ---
  async listPlanCatalog() {
    const response = await request('/credits/plans', { method: 'GET' });
    return response?.plans || [];
  },

  // --- Modelos configurados (vigentes y activos, sin precios; cualquier
  // usuario autenticado, para la página "Modelos LLM") ---
  async listConfiguredModels() {
    const response = await request('/credits/models', { method: 'GET' });
    return Array.isArray(response?.models) ? response.models : [];
  },

  // --- Cambio de plan self-service (solo planes gratis; admin del tenant o root) ---
  async switchToPlan({ accountId, planId }) {
    return request('/account/plan', {
      method: 'PUT',
      body: JSON.stringify({ account_id: accountId, plan_id: planId })
    });
  },

  // --- Checkout de Stripe para planes pagos (admin del tenant o root) ---
  async createCheckoutSession({ accountId, planId, successUrl, cancelUrl }) {
    return request('/account/checkout-session', {
      method: 'POST',
      body: JSON.stringify({
        account_id: accountId, plan_id: planId, success_url: successUrl, cancel_url: cancelUrl
      })
    });
  },

  // --- Portal de facturación de Stripe (admin del tenant o root) ---
  async createPortalSession({ accountId, returnUrl }) {
    return request('/account/portal-session', {
      method: 'POST',
      body: JSON.stringify({ account_id: accountId, return_url: returnUrl })
    });
  }
};

export default CreditsEngineService;
